import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  computed,
  effect,
  inject,
} from '@angular/core';
import { SeoService } from '@kirigami/platform/browser';
import { TranslatePipe, TranslationService } from '@kirigami/shared/i18n';
import { UploadnowWidgetComponent } from './uploadnow-widget.component';

/** One guidance step, its copy resolved for the active locale. */
interface UploadStep {
  readonly title: string;
  readonly body: string;
}

/** The steps in reading order, addressing the copy under `landing.upload.steps.<slug>`. */
const UPLOAD_STEP_SLUGS = ['draft', 'analysis', 'report'] as const;

/**
 * Upload section, "Your guidance starts here": a pale blue panel with the
 * owner's origami bird from Figma ("Kirigami - Design - Tech", Social Media
 * carousel, frame 6, node 7641:5301) beside the dropzone, and the three steps
 * underneath as a static row. Nothing moves on its own.
 *
 * All upload behaviour lives in the embedded <app-uploadnow-widget> and is
 * unchanged: file validation, the disabled state and the login gate. Only its
 * surface is restyled from here, so the dropzone reads as a white card on the
 * panel.
 */
@Component({
  selector: 'app-upload-section-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UploadnowWidgetComponent, TranslatePipe],
  template: `
    <section id="upload-now" aria-labelledby="upload-title" class="upload">
      <div class="upload__inner">
        <h2 id="upload-title" class="upload__title">{{ 'landing.upload.title' | t }}</h2>

        <div class="upload__panel">
          <div class="upload__art" aria-hidden="true">
            <img
              class="upload__art-img"
              src="img/upload-bird.webp"
              alt=""
              width="772"
              height="2036"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div class="upload__drop">
            <app-uploadnow-widget
              [isAuthenticated]="isAuthenticated"
              [disabled]="disabled"
              (fileSelected)="selectFile($event)"
              (loginRequired)="loginRequiredOut.emit()"
            />
            @if (disabled) {
              <p class="upload__busy" role="status">{{ 'landing.upload.busy' | t }}</p>
            }
          </div>
        </div>

        <ol class="upload__steps" role="list" [attr.aria-label]="'landing.upload.stepsAria' | t">
          @for (uploadStep of steps(); track uploadStep.title; let i = $index) {
            <li class="upload__step">
              <span class="upload__step-number">{{ i + 1 }}</span>
              <div class="upload__step-body">
                <h3 class="upload__step-title">{{ uploadStep.title }}</h3>
                <p class="upload__step-text">{{ uploadStep.body }}</p>
              </div>
            </li>
          }
        </ol>
      </div>
    </section>
  `,
  styles: [
    `
      .upload {
        background: var(--neu-0);
        padding: 6rem 1rem 6.5rem;
      }
      @media (min-width: 480px) {
        .upload {
          padding: 7rem 1.5rem 7.5rem;
        }
      }
      @media (min-width: 1024px) {
        .upload {
          padding: 7.5rem 4.0625rem 8.125rem;
        }
      }
      .upload__inner {
        max-width: 72rem;
        margin: 0 auto;
      }
      .upload__title {
        margin: 0 0 3rem;
        text-align: center;
        font-size: clamp(2rem, 4vw, 2.5rem);
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: -0.011em;
        text-wrap: balance;
        color: var(--neu-900);
      }
      @media (min-width: 1024px) {
        .upload__title {
          margin-bottom: 3.5rem;
        }
      }

      /* ── The panel: bird on the left, dropzone on the right ── */
      .upload__panel {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: center;
        gap: 1.5rem;
        padding: 1.75rem 1rem 1.5rem;
        border-radius: 1.5rem;
        background: var(--kirigami-50);
      }
      @media (min-width: 480px) {
        .upload__panel {
          padding: 2rem 1.5rem 1.75rem;
        }
      }
      @media (min-width: 1024px) {
        .upload__panel {
          grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
          gap: 3rem;
          padding: 3rem 3.5rem 3rem 2.5rem;
        }
      }

      /* The bird as Figma frames it: a 772 x 639 band of the photo (its top
         78.25% above), at 80% over the pale blue. The photo's own background
         is a shade lighter than the panel's, so its edges fade out instead of
         showing as a box. The bird reaches close to the band's edges, hence
         a narrow fade. */
      .upload__art {
        position: relative;
        width: 100%;
        max-width: 15rem;
        justify-self: center;
        aspect-ratio: 772 / 639;
        overflow: hidden;
        -webkit-mask-image:
          linear-gradient(to right, transparent, #000 4%, #000 96%, transparent),
          linear-gradient(to bottom, transparent, #000 4%, #000 96%, transparent);
        -webkit-mask-composite: source-in;
        mask-image:
          linear-gradient(to right, transparent, #000 4%, #000 96%, transparent),
          linear-gradient(to bottom, transparent, #000 4%, #000 96%, transparent);
        mask-composite: intersect;
      }
      @media (min-width: 768px) {
        .upload__art {
          max-width: 20rem;
        }
      }
      @media (min-width: 1024px) {
        .upload__art {
          max-width: 29rem;
        }
      }
      .upload__art-img {
        position: absolute;
        left: 0;
        top: -78.25%;
        width: 100%;
        height: auto;
        opacity: 0.8;
      }

      .upload__busy {
        margin: 0.75rem 0 0;
        color: var(--neu-700);
        font-size: 0.875rem;
        line-height: 1.5;
        text-align: center;
      }

      /* The embedded dropzone as a white card on the panel. Its hover tint
         (kirigami-100) and its disabled look come from the widget itself;
         the focus ring is restated because the card's shadow would otherwise
         replace it, and the wrapper stops clipping so the ring shows whole. */
      :host ::ng-deep .upload__drop .upload-now {
        overflow: visible;
        border-radius: 1.25rem;
      }
      :host ::ng-deep .upload__drop .upload-now__button {
        min-height: 17rem;
        padding: 2rem 1rem;
        border: 1.5px dashed var(--kirigami-300);
        border-radius: 1.25rem;
        background: var(--neu-0);
        box-shadow: 0 8px 24px rgba(45, 85, 121, 0.08);
      }
      @media (min-width: 480px) {
        :host ::ng-deep .upload__drop .upload-now__button {
          padding: 2.5rem 2rem;
        }
      }
      @media (min-width: 1024px) {
        :host ::ng-deep .upload__drop .upload-now__button {
          min-height: 21rem;
          box-shadow: 0 18px 48px rgba(45, 85, 121, 0.1);
        }
      }
      :host ::ng-deep .upload__drop .upload-now__button--disabled {
        background: var(--kirigami-50);
        box-shadow: none;
      }
      :host ::ng-deep .upload__drop .upload-now__button:focus-visible {
        box-shadow:
          inset 0 0 0 1px var(--neu-0),
          0 0 0 2px var(--neu-0),
          0 0 0 4px var(--kirigami-600);
      }
      :host ::ng-deep .upload__drop .upload-now__icon {
        width: 6.25rem;
      }

      /* ── The three steps: a row joined by a hairline, a column on phones ── */
      .upload__steps {
        list-style: none;
        margin: 3rem 0 0;
        padding: 0 0.25rem;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 2rem;
      }
      .upload__step {
        position: relative;
        display: grid;
        grid-template-columns: 2.25rem minmax(0, 1fr);
        column-gap: 1rem;
        align-items: start;
        align-content: start;
      }
      .upload__step:not(:last-child)::after {
        content: '';
        position: absolute;
        left: 1.125rem;
        top: 3rem;
        bottom: -1.25rem;
        width: 1px;
        background: var(--kirigami-200);
      }
      @media (min-width: 768px) {
        .upload__steps {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 2.5rem;
          margin-top: 3.5rem;
          padding: 0 1rem;
        }
        .upload__step {
          grid-template-columns: minmax(0, 1fr);
          row-gap: 1.25rem;
        }
        .upload__step:not(:last-child)::after {
          left: 3rem;
          right: -1.75rem;
          top: 1.125rem;
          bottom: auto;
          width: auto;
          height: 1px;
        }
      }
      .upload__step-number {
        width: 2.25rem;
        height: 2.25rem;
        border: 1px solid var(--kirigami-300);
        border-radius: 999px;
        background: var(--neu-0);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--kirigami-700);
      }
      .upload__step-title {
        margin: 0.375rem 0 0;
        font-size: 1.125rem;
        font-weight: 700;
        line-height: 1.35;
        color: var(--neu-900);
      }
      @media (min-width: 768px) {
        .upload__step-title {
          margin-top: 0;
        }
      }
      .upload__step-text {
        margin: 0.5rem 0 0;
        font-size: 0.9375rem;
        line-height: 1.6;
        color: var(--neu-700);
        text-wrap: pretty;
      }
    `,
  ],
})
export class UploadSectionWidgetComponent {
  private readonly seo = inject(SeoService);
  private readonly translations = inject(TranslationService);

  constructor() {
    // The three steps as HowTo, generated from the same dictionary entries the
    // list renders so the markup cannot drift from the page. Search engines
    // have largely stopped showing HowTo results; this is here because it
    // describes real content and costs nothing.
    effect(() => {
      this.seo.setJsonLd('howto', {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: this.translations.t('landing.upload.title'),
        step: this.steps().map((uploadStep, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: uploadStep.title,
          text: uploadStep.body,
        })),
      });
    });
    inject(DestroyRef).onDestroy(() => this.seo.removeJsonLd('howto'));
  }

  @Input() isAuthenticated = false;

  /** Disables the dropzone and shows the "analysis in progress" message. */
  @Input() disabled = false;

  @Output() readonly fileSelectedOut = new EventEmitter<File>();
  @Output() readonly loginRequiredOut = new EventEmitter<void>();

  /**
   * The steps with their copy resolved for the active locale. Resolved here
   * rather than in the template because a `@for` cannot name a key literally.
   */
  readonly steps = computed<readonly UploadStep[]>(() =>
    UPLOAD_STEP_SLUGS.map((slug) => ({
      title: this.translations.t(`landing.upload.steps.${slug}.title`),
      body: this.translations.t(`landing.upload.steps.${slug}.body`),
    })),
  );

  selectFile(file: File) {
    this.fileSelectedOut.emit(file);
  }
}
